import { styled } from '@aether/styles'
import { FC, useState, useRef, useEffect } from 'react'
import { Button } from '../Button/Button'

const DropdownContainer = styled('div', {
  position: 'relative',
  display: 'inline-block',
})

const DropdownWrapper = styled('div', {
  position: 'absolute',
  top: '100%',
  left: 0,
  width: '100%',
  zIndex: '$stickyButton',
  opacity: 0,
  transform: 'translateY(-10px)',
  transition: 'opacity 0.2s ease, transform 0.2s ease',
  pointerEvents: 'none',

  '&[data-visible="true"]': {
    opacity: 1,
    transform: 'translateY(0)',
    pointerEvents: 'auto',
  },
})

const DropdownMenu = styled('div', {
  width: '100%',
  overflow: 'visible',

  '& > *': {
    width: '100%',
    maxWidth: '100%',
  },
})

const StyledButton = styled(Button, {
  marginTop: '$12',
})

interface ItemComponentProps {
  content: React.ReactNode
  onClick?: () => void
  isSelected?: boolean
}

const DefaultDropdownItem: FC<ItemComponentProps> = ({
  content,
  onClick,
  isSelected,
}) => {
  return (
    <StyledButton
      appearance="badgeOutlineBig"
      onClick={onClick}
      role="option"
      aria-selected={isSelected}
      tabIndex={isSelected ? 0 : -1}
    >
      {content}
    </StyledButton>
  )
}

interface DropdownProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  trigger: React.ReactNode
  label?: string
  items: Array<{
    id: string | number
    content: React.ReactNode
    onClick?: () => void
  }>
  itemComponent?: FC<ItemComponentProps>
}

export const Dropdown: FC<DropdownProps> = ({
  isOpen,
  onOpenChange,
  trigger,
  label,
  items,
  itemComponent: ItemComponent = DefaultDropdownItem,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const triggerRef = useRef<HTMLDivElement>(null)
  const [isOpenedByKeyboard, setIsOpenedByKeyboard] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0)
      if (isOpenedByKeyboard) {
        // Focus the first item when dropdown opens via keyboard
        setTimeout(() => {
          const firstItem =
            dropdownRef.current?.querySelector('[role="option"]')
          if (firstItem instanceof HTMLElement) {
            firstItem.focus()
          }
        }, 0)
      }
    } else {
      setActiveIndex(-1)
      // Return focus to trigger when dropdown closes
      triggerRef.current?.focus()
    }
  }, [isOpen, isOpenedByKeyboard])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onOpenChange(false)
      }
    }

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onOpenChange])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (
        event.key === 'Enter' ||
        event.key === ' ' ||
        event.key === 'ArrowDown'
      ) {
        event.preventDefault()
        setIsOpenedByKeyboard(true)
        onOpenChange(true)
      }
      return
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        const nextIndex =
          activeIndex < items.length - 1 ? activeIndex + 1 : activeIndex
        setActiveIndex(nextIndex)
        const nextItem =
          dropdownRef.current?.querySelectorAll('[role="option"]')[nextIndex]
        if (nextItem instanceof HTMLElement) {
          nextItem.focus()
        }
        break
      }
      case 'ArrowUp': {
        event.preventDefault()
        const prevIndex = activeIndex > 0 ? activeIndex - 1 : activeIndex
        setActiveIndex(prevIndex)
        const prevItem =
          dropdownRef.current?.querySelectorAll('[role="option"]')[prevIndex]
        if (prevItem instanceof HTMLElement) {
          prevItem.focus()
        }
        break
      }
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (activeIndex >= 0) {
          const activeItem = items[activeIndex]
          if (activeItem?.onClick) {
            activeItem.onClick()
          }
        }
        onOpenChange(false)
        break
      case 'Escape':
        event.preventDefault()
        onOpenChange(false)
        break
      case 'Tab':
        onOpenChange(false)
        break
    }
  }

  const handleBlur = (event: React.FocusEvent) => {
    // Check if the focus is moving outside the dropdown
    if (!dropdownRef.current?.contains(event.relatedTarget as Node)) {
      onOpenChange(false)
    }
  }

  return (
    <DropdownContainer
      ref={dropdownRef}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-label={label}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    >
      <div
        ref={triggerRef}
        onClick={() => {
          setIsOpenedByKeyboard(false)
          onOpenChange(!isOpen)
        }}
        aria-controls="dropdown-menu"
      >
        {trigger}
      </div>
      <DropdownWrapper data-visible={isOpen}>
        <DropdownMenu
          id="dropdown-menu"
          role="listbox"
          aria-label="Dropdown options"
        >
          {items.map((item, index) => (
            <ItemComponent
              key={item.id}
              content={item.content}
              onClick={() => {
                if (item.onClick) {
                  item.onClick()
                }
                onOpenChange(false)
              }}
              isSelected={index === activeIndex}
            />
          ))}
        </DropdownMenu>
      </DropdownWrapper>
    </DropdownContainer>
  )
}
